import { apiRequest, apiRequestEnvelope } from '@scc/lib'
import type { VideoRepository } from '@/data/repositories/types'
import { toApiDate } from '@/lib/api-date'
import type { DataFreshness, DateRange } from '@/modules/shared/types'
import type { VideoDailyMetric, VideoDetail, VideoListRow, VideoSortDirection } from '@/modules/videos/types'

const videoListFreshness = new Map<string, DataFreshness>()
const videoListPageSize = new Map<string, number>()
const videoDetailFreshness = new Map<string, DataFreshness>()

export function getVideoListFreshness(clientId: string, range: DateRange): DataFreshness | null {
  return videoListFreshness.get(`${clientId}:${toApiDate(range.from)}:${toApiDate(range.to)}`) ?? null
}

export function getVideoListPageSize(clientId: string, range: DateRange): number | null {
  return videoListPageSize.get(`${clientId}:${toApiDate(range.from)}:${toApiDate(range.to)}`) ?? null
}

export function getVideoDetailFreshness(clientId: string, videoId: string, range: DateRange): DataFreshness | null {
  return (
    videoDetailFreshness.get(`${clientId}:${videoId}:${toApiDate(range.from)}:${toApiDate(range.to)}`) ?? null
  )
}

export function createApiVideoRepository(): VideoRepository {
  return {
    async getDailyMetrics(clientId: string, range: DateRange): Promise<VideoDailyMetric[]> {
      const start = toApiDate(range.from)
      const end = toApiDate(range.to)
      const rows = await apiRequest<VideoDailyMetric[]>(
        `/analytics/videos/daily?start=${start}&end=${end}`,
        { headers: { 'X-Store-Id': clientId } },
      )
      return rows
    },

    async getVideoList(
      clientId: string,
      range: DateRange,
      sort: string,
      direction: VideoSortDirection,
      page: number,
    ): Promise<VideoListRow[]> {
      const { data, meta } = await apiRequestEnvelope<
        VideoListRow[],
        { freshness?: DataFreshness; pageSize?: number } | undefined
      >(
        `/analytics/videos/list?start=${toApiDate(range.from)}&end=${toApiDate(range.to)}&sort=${sort}&direction=${direction}&page=${page}`,
        { headers: { 'X-Store-Id': clientId } },
      )
      // Keyed per (store, range) like shop.api.ts — a single "last" value would
      // be racy across stores. The Videos tab reads this to render the
      // settling-data notice and to size its pagination.
      const key = `${clientId}:${toApiDate(range.from)}:${toApiDate(range.to)}`
      if (meta?.freshness) {
        videoListFreshness.set(key, meta.freshness)
      }
      if (typeof meta?.pageSize === 'number') {
        videoListPageSize.set(key, meta.pageSize)
      }
      return data ?? []
    },

    async getVideoDetail(clientId: string, videoId: string, range: DateRange): Promise<VideoDetail> {
      const { data, meta } = await apiRequestEnvelope<VideoDetail, { freshness?: DataFreshness } | undefined>(
        `/analytics/videos/${encodeURIComponent(videoId)}?start=${toApiDate(range.from)}&end=${toApiDate(range.to)}`,
        { headers: { 'X-Store-Id': clientId } },
      )
      // Keyed per (store, video, range) like getVideoList — the detail page
      // reads this to render the settling-data notice above the trend chart.
      if (meta?.freshness) {
        const key = `${clientId}:${videoId}:${toApiDate(range.from)}:${toApiDate(range.to)}`
        videoDetailFreshness.set(key, meta.freshness)
      }
      return data
    },
  }
}

export const videoRepository = createApiVideoRepository()
